import styled from 'styled-components';
import { i18n } from '../../i18n';
import { maskPhoneNumber, momentTextFormat } from '../../utils';
import { useStoreUser } from '../../context';
import { CURRENCIES } from '../../const';
import { CurrencyCode } from '../../types';

const Root = styled.div`
  display: none;

  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow: auto;
  background: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1.2rem;
  line-height: 1.45;
  color: #555555;
  -webkit-font-smoothing: antialiased;

  @media print {
    display: block;

    @page {
      size: A4;
      margin: 0;
    }
  }
`;

const Page = styled.div`
  display: grid;
  grid-template-rows: repeat(5, max-content) 1fr;

  box-sizing: border-box;
  min-height: 100%;
  max-width: 82rem;
  margin: 0 auto;
  padding: 3.6rem 4rem 4.8rem;
`;

const HeaderBand = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2.4rem;
  background: #f2f2f2;
  padding: 2rem 2.4rem;
  margin-bottom: 2rem;
`;

const InvoiceTitle = styled.div`
  font-size: 3.6rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #555555;
  line-height: 1;
  text-transform: uppercase;
`;

const CompanyBlock = styled.div`
  flex: 1;
  text-align: right;
  font-size: 1.2rem;
  color: #555555;
`;

const LogoCircle = styled.div`
  flex-shrink: 0;
  width: 7.2rem;
  height: 7.2rem;
  border-radius: 50%;
  background: #555555;
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.6rem;
  margin-bottom: 2.4rem;
`;

const MetaLeft = styled.div`
  font-weight: 700;
  text-transform: uppercase;
`;

const MetaRight = styled.div`
  text-align: right;
`;

const DateLine = styled.div`
  font-weight: 700;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
`;

const PaymentTerms = styled.div`
  font-size: 1.1rem;
  font-style: italic;
  color: #666666;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3.2rem;
  margin-bottom: 2.8rem;
`;

const AddressColumn = styled.div``;

const AddressHeading = styled.div`
  font-weight: 700;
  margin-bottom: 0.8rem;
`;

const AddressRule = styled.div`
  height: 0.1rem;
  background: #dddddd;
  margin-bottom: 1rem;
`;

const AddressLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 1.2rem;
`;

const AddressLine = styled.div``;

const Table = styled.div`
  border: 0.1rem solid #dddddd;
  margin-bottom: 2rem;
`;

const TableHead = styled.div`
  display: grid;
  /* grid-template-columns: 1fr 7.2rem 11rem 9rem; */
  grid-template-columns: 1fr 7.2rem 11rem;
  background: #888888;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Th = styled.div<{ $align?: 'left' | 'center' | 'right' }>`
  padding: 1rem 1.2rem;
  text-align: ${(p) => p.$align ?? 'left'};
  border-right: 0.1rem solid #aaaaaa;

  &:last-child {
    border-right: none;
  }
`;

const TableRow = styled.div<{ $striped?: boolean }>`
  display: grid;
  /* grid-template-columns: 1fr 7.2rem 11rem 9rem; */
  grid-template-columns: 1fr 7.2rem 11rem;
  background: ${(p) => (p.$striped ? '#f2f2f2' : '#ffffff')};
  border-top: 0.1rem solid #dddddd;
`;

const Td = styled.div<{ $align?: 'left' | 'center' | 'right' }>`
  padding: 1rem 1.2rem;
  text-align: ${(p) => p.$align ?? 'left'};
  border-right: 0.1rem solid #dddddd;
  min-height: 1.8rem;

  &:last-child {
    border-right: none;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3.2rem;
  margin-top: 0.8rem;
`;

const Remarks = styled.div`
  flex: 1;
  font-weight: 700;
  font-size: 1.2rem;
`;

const TotalsWrap = styled.div`
  width: 28rem;
  flex-shrink: 0;
`;

const TotalLine = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 1.6rem;
  padding: 0.4rem 0;
  font-size: 1.2rem;
`;

const TotalLabel = styled.span`
  text-align: right;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const TotalValue = styled.span`
  min-width: 7.2rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const BalanceBar = styled.div`
  margin-top: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #555555;
  color: #ffffff;
  padding: 1.4rem 1.6rem;
  font-weight: 700;
`;

const BalanceLeft = styled.span`
  font-size: 1.3rem;
`;

const BalanceAmount = styled.span`
  font-size: 2.2rem;
  font-variant-numeric: tabular-nums;
`;

const FooterLinkWrap = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: left;
  font-size: 1.8rem;
  color: #777777;
  word-break: break-all;
`;

const FooterLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

const getRows = (invoice: any) => {
  const storage = invoice.lines.reduce(
    (acc: any, line: any) => {
      acc.total += line.storageAmount;
      acc.qty += 1;
      return acc;
    },
    {
      desc: `${i18n.t('dropdown.storage')} ${i18n.t('placeholder.days')}`,
      qty: 0,
      total: 0,
    },
  );

  const crane = invoice.lines.reduce(
    (acc: any, line: any) => {
      acc.total += line.gateInCraneAmount + line.gateOutCraneAmount;
      if (line.gateInCraneAmount > 0) {
        acc.qty += 1;
      }
      if (line.gateOutCraneAmount > 0) {
        acc.qty += 1;
      }
      return acc;
    },
    {
      desc: i18n.t('dropdown.crane'),
      qty: 0,
      total: 0,
    },
  );

  const other = invoice.lines.reduce(
    (acc: any, line: any) => {
      acc.total += line.otherAmount;
      acc.qty += 1;
      return acc;
    },
    {
      desc: i18n.t('dropdown.other'),
      qty: 0,
      total: 0,
    },
  );

  const emptyRow = {
    desc: 'N / A',
    qty: '-',
    total: '-',
  };

  let rows = [];
  switch (invoice.type) {
    case 'STORAGE':
      rows = [storage, emptyRow, emptyRow];
      break;
    case 'CRANE':
      rows = [crane, emptyRow, emptyRow];
      break;
    case 'OTHER':
      rows = [other, emptyRow, emptyRow];
      break;
    default:
      rows = [storage, crane, emptyRow];
      break;
  }

  return rows.map((r) => ({
    ...r,
    total: typeof r.total === 'number' ? r.total.toFixed(2) : r.total,
  }));
};

export const InvoicePrint = ({ invoice }: any) => {
  const { user } = useStoreUser();

  const client = invoice.clientYard.client;
  const yard = invoice.clientYard.yard;
  const props = {
    invoiceNumber: invoice.invoiceNumber,
    date: `${momentTextFormat(invoice.periodStart, user, true)} --- ${momentTextFormat(
      invoice.periodEnd,
      user,
      true,
    )}`,
    billTo: {
      contactName: `${client.contactPersonFirstName} ${client.contactPersonLastName}`,
      clientCompanyName: `${client.legalName} (${client.code})`,
      address: `${client.addressCountry} ${client.addressCity}`,
      phone: `${maskPhoneNumber(String(client.phoneNumber))}, ${maskPhoneNumber(
        String(client.contactPersonPhoneNumber),
      )}`,
      email: client.email,
    },
    shipTo: {
      name: `${yard.name} (${yard.code})`,
      address: `${yard.addressCountry} ${yard.addressCity}`,
      phone: maskPhoneNumber(String(yard.phoneNumber)),
    },
    rows: getRows(invoice),
    balanceDue: `${
      CURRENCIES[invoice.currencyCode as CurrencyCode].symbol
    }${invoice.totalAmount.toFixed(2)}`,
    footerLink: window.location.href,
  };

  return (
    <Root>
      <Page>
        <HeaderBand>
          <InvoiceTitle>
            {i18n.t('pages.invoicesView.title')}: {invoice.type}
          </InvoiceTitle>
          <CompanyBlock>
            {/* <CompanyName>&lt;Your Company Name&gt;</CompanyName>
            <div>&lt;Your address&gt;</div>
            <div>&lt;Your contact details&gt;</div> */}
          </CompanyBlock>
          <LogoCircle>TEZPORT</LogoCircle>
        </HeaderBand>

        <MetaRow>
          <MetaLeft>
            {i18n.t('pages.invoicesView.title')} - {invoice.invoiceNumber}
          </MetaLeft>
          <MetaRight>
            <DateLine>
              {i18n.t('pages.invoicesView.date')}: {props.date}
            </DateLine>
            <PaymentTerms>&lt;{i18n.t('pages.invoicesView.subDate')}&gt;</PaymentTerms>
          </MetaRight>
        </MetaRow>

        <AddressGrid>
          <AddressColumn>
            <AddressHeading>{i18n.t('common.client')}</AddressHeading>
            <AddressRule />
            <AddressLines>
              <AddressLine>{props.billTo.contactName}</AddressLine>
              <AddressLine>{props.billTo.clientCompanyName}</AddressLine>
              <AddressLine>{props.billTo.address}</AddressLine>
              <AddressLine>{props.billTo.phone}</AddressLine>
              <AddressLine>{props.billTo.email}</AddressLine>
            </AddressLines>
          </AddressColumn>
          <AddressColumn>
            <AddressHeading>{i18n.t('common.yard')}</AddressHeading>
            <AddressRule />
            <AddressLines>
              <AddressLine>{props.shipTo.name}</AddressLine>
              <AddressLine>{props.shipTo.address}</AddressLine>
              <AddressLine>{props.shipTo.phone}</AddressLine>
            </AddressLines>
          </AddressColumn>
        </AddressGrid>

        <Table>
          <TableHead>
            <Th>{i18n.t('label.description')}</Th>
            <Th $align="center">{i18n.t('label.qty')}</Th>
            {/* <Th $align="right">UNIT PRICE</Th> */}
            <Th $align="right">{i18n.t('label.total')}</Th>
          </TableHead>
          {props.rows.map((r, i) => (
            <TableRow key={i} $striped={i % 2 === 1}>
              <Td>{r.desc}</Td>
              <Td $align="center">{r.qty}</Td>
              {/* <Td $align="right">{r.unit}</Td> */}
              <Td $align="right">{r.total}</Td>
            </TableRow>
          ))}
        </Table>

        <BottomSection>
          <Remarks>{i18n.t('pages.invoicesView.remarks')}</Remarks>
          <TotalsWrap>
            <TotalLine>
              <TotalLabel>{i18n.t('pages.invoicesView.subtotal')}</TotalLabel>
              <TotalValue>{invoice.totalAmount.toFixed(2)}</TotalValue>
            </TotalLine>
            {/* <TotalLine>
              <TotalLabel>DISCOUNT</TotalLabel>
              <TotalValue>2.00</TotalValue>
            </TotalLine>
            <TotalLine>
              <TotalLabel>SUBTOTAL LESS DISCOUNT</TotalLabel>
              <TotalValue>18.00</TotalValue>
            </TotalLine>
            <TotalLine>
              <TotalLabel>TAX RATE</TotalLabel>
              <TotalValue>8.00%</TotalValue>
            </TotalLine>
            <TotalLine>
              <TotalLabel>TOTAL TAX</TotalLabel>
              <TotalValue>1.44</TotalValue>
            </TotalLine>
            <TotalLine>
              <TotalLabel>SHIPPING/HANDLING</TotalLabel>
              <TotalValue>0.00</TotalValue>
            </TotalLine> */}
            <BalanceBar>
              <BalanceLeft>{i18n.t('pages.invoicesView.balanceDue')}</BalanceLeft>
              <BalanceAmount>{props.balanceDue}</BalanceAmount>
            </BalanceBar>
          </TotalsWrap>
        </BottomSection>
        <FooterLinkWrap>
          <FooterLink href={props.footerLink} target="_blank" rel="noreferrer">
            {props.footerLink}
          </FooterLink>
        </FooterLinkWrap>
      </Page>
    </Root>
  );
};
